import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { alterPlaylistGroups } from '@disco/state-management/actions/ExplorerActions'
import {
  getGroup,
  getGroupsWithNesting,
} from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { sendPlaylistDownloadEventThrottled } from '@disco/utils/eventsApi'

import AddToChannelModal from 'components/common/custom-libraries/add-to-channel'
import DocumentHeadTags from 'components/document-head-tags'

import type { RootState } from 'store/configureStore'

interface OwnProps {
  params: {
    playlistId: string
  }
  location: Location
  navigate: NavigateFunction
  searchParams: URLSearchParams
}

function mapStateToProps(state: RootState, props: OwnProps) {
  return {
    playlist: getPlaylist(state, {
      id: parseInt(props.params.playlistId, 10),
    }),
    sourceGroup: getGroup(state, {
      groupId: props.searchParams.get('source_group')
        ? parseInt(props.searchParams.get('source_group'), 10)
        : undefined,
    }),
    groups: getGroupsWithNesting(state),
  }
}

class AddToChannel extends React.Component<Props> {
  _onAddToGroup = (groupsToAdd: Record<string, any>[]) => {
    const existingGroups = groupsToAdd
      .filter(groupToAdd => !groupToAdd.created)
      .map(groupToAdd => {
        return this.props.groups.find(group => group.id === groupToAdd.value)
      })
      .filter(Boolean)

    if (existingGroups.length) {
      this.props.alterPlaylistGroups([this.props.playlist], existingGroups, [])
      this.props.sendMixpanelEvent('Add to channel from playlist menu')
    }

    if (
      this.props.sourceGroup &&
      INBOX_TYPES.includes(this.props.sourceGroup.group_type)
    ) {
      sendPlaylistDownloadEventThrottled(
        this.props.playlist.playlist_id,
        undefined,
        this.props.sourceGroup.id,
      )
    }

    this._closeModal()
  }
  _closeModal = () => this.props.navigate('/')

  render() {
    return (
      <div>
        <DocumentHeadTags title="Add to channel | DISCO" />
        <AddToChannelModal
          groups={this.props.groups}
          source={this.props.playlist}
          onSubmit={this._onAddToGroup}
          onClose={this._closeModal}
        />
      </div>
    )
  }
}

const connector = connect(mapStateToProps, {
  sendMixpanelEvent,
  alterPlaylistGroups,
})

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux & OwnProps

const ConnectedAddToChannel = connector(AddToChannel)

function AddToChannelWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ playlistId: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  return (
    <ConnectedAddToChannel
      params={params as { playlistId: string }}
      location={location}
      searchParams={searchParams}
      navigate={navigate}
    />
  )
}

export default AddToChannelWithRouter
